import client from '@/graphql/client'
import ListUsers from '@/graphql/queries/ListUsers'
import ListWorkflows from '@/graphql/queries/ListWorkflows'
import ListTeamMembers from '@/graphql/queries/ListTeamMembers'
import CreateUser from '@/graphql/mutations/CreateUser'
import UpdateUser from '@/graphql/mutations/UpdateUser'
import AddAdminNote from '@/graphql/mutations/AddAdminNote'
import GetCurrentUser from '@/graphql/queries/GetCurrentUser'
import GenerateApiKey from '@/graphql/mutations/GenerateApiKey'
import RevokeApiKey from '@/graphql/mutations/RevokeApiKey'
import SetAiProviderKey from '@/graphql/mutations/SetAiProviderKey'
import RemoveAiProviderKey from '@/graphql/mutations/RemoveAiProviderKey'
import CheckAiProviderKey from '@/graphql/mutations/CheckAiProviderKey'
import brand from '@/composables/useBrand'

const KEY_STATUS_MESSAGE = {
  ACTIVE: 'API key is working.',
  REJECTED: 'The provider rejected this API key.',
  QUOTA_EXCEEDED: `This key has no credits left. ${brand.name}'s key is used until you top it up.`
}

const UserSortableField = {
  REGISTRATION_DATE: 'REGISTRATION_DATE',
  LAST_LOGIN: 'LAST_LOGIN',
  LOGIN_COUNTER: 'LOGIN_COUNTER'
}

const SortOrder = {
  ASC: 'ASC',
  DESC: 'DESC'
}

let latestListUsersRequestId = 0

async function fetchRemainingNestedPages (users) {
  for (const user of users) {
    if (!user.organisations) {
      continue
    }

    for (const org of user.organisations) {
      if (!org.projects) {
        continue
      }

      await Promise.all(org.projects.map(async (project) => {
        if (project.workflows?.nextToken) {
          let nextToken = project.workflows.nextToken
          while (nextToken) {
            const pageRes = await client.query({
              operationName: 'listWorkflows',
              query: ListWorkflows,
              variables: { projectId: project.id, limit: 100, nextToken }
            }, { failSilently: true })
            const page = pageRes?.listWorkflows?.project?.workflows
            project.workflows.items = (project.workflows.items || []).concat(page?.items || [])
            nextToken = page?.nextToken
          }
          project.workflows.nextToken = null
        }

        if (project.teamMembers?.nextToken) {
          let nextToken = project.teamMembers.nextToken
          while (nextToken) {
            const pageRes = await client.query({
              operationName: 'listTeamMembers',
              query: ListTeamMembers,
              variables: { projectId: project.id, limit: 100, nextToken }
            }, { failSilently: true })
            const page = pageRes?.listTeamMembers
            project.teamMembers.items = (project.teamMembers.items || []).concat(page?.items || [])
            nextToken = page?.nextToken
          }
          project.teamMembers.nextToken = null
        }
      }))
    }
  }
}

export const user = {
  namespaced: true,
  state: {
    user: null,
    users: null,
    nextTokenUsers: null,
    loadingUsers: false,
    searchTerm: '',
    sortBy: {
      field: UserSortableField.REGISTRATION_DATE,
      order: SortOrder.DESC
    }
  },
  mutations: {
    setUser (state, payload) {
      state.user = payload
    },
    updateUser (state, payload) {
      state.user = payload
    },
    setUsers (state, { items, append = false }) {
      if (append) {
        state.users = [...(state.users || []), ...items]
      } else {
        state.users = items
      }
    },
    setNextTokenUsers (state, token) {
      state.nextTokenUsers = token
    },
    setLoadingUsers (state, isLoading) {
      state.loadingUsers = isLoading
    },
    setCurrentSearchTerm (state, term) {
      state.searchTerm = term
    },
    setCurrentSortBy (state, sortBy) {
      state.sortBy = sortBy
    },
    updateUserNotes (state, { userId, adminNotes }) {
      if (!state.users) {
        return
      }

      const userIndex = state.users.findIndex((user) => user.id === userId)

      if (userIndex !== -1) {
        state.users[userIndex].adminNotes = adminNotes
      }
    }
  },
  actions: {
    async createUser ({ commit }, input) {
      try {
        const res = await client.query({
          operationName: 'createUser',
          query: CreateUser,
          variables: {
            ...input
          }
        })
        commit('setUser', res.createUser)
      } catch (e) {
        commit('snackbar/showMessage', { content: 'An error occurred while creating user', timeout: 6000, color: 'red' }, { root: true })
      }
    },

    async updateUser ({ commit, state }, input) {
      try {
        const res = await client.query({
          operationName: 'updateUser',
          query: UpdateUser,
          variables: {
            ...input
          }
        })
        commit('setUser', {
          ...res.updateUser,
          hasMcpApiKey: state.user?.hasMcpApiKey,
          apiKeyExpiresAt: state.user?.apiKeyExpiresAt,
          aiProviderKeys: state.user?.aiProviderKeys
        })
      } catch (e) {
        commit('snackbar/showMessage', { content: 'An error occurred while updating user', timeout: 6000, color: 'red' }, { root: true })
      }
    },

    async addAdminNote ({ commit }, input) {
      try {
        const response = await client.query({
          operationName: 'addAdminNote',
          query: AddAdminNote,
          variables: input
        })

        const updatedUser = response.addAdminNote

        if (updatedUser && updatedUser.adminNotes) {
          commit('updateUserNotes', {
            userId: updatedUser.id,
            adminNotes: updatedUser.adminNotes
          })
        }
      } catch (e) {
        commit('snackbar/showMessage', {
          content: 'Error adding note',
          timeout: 6000,
          color: 'red'
        }, { root: true })
      }
    },

    async getCurrentUser ({ commit }) {
      const res = await client.query({
        operationName: 'getCurrentUser',
        query: GetCurrentUser
      })
      const currentUser = res.getCurrentUser

      commit('setUser', currentUser)
      commit('github/setGithubAccounts', currentUser?.githubAccounts || [], { root: true })

      return res
    },

    setAiProviderKeyEntry ({ commit, state }, { provider, mask, status = null }) {
      const others = (state.user?.aiProviderKeys || []).filter((k) => k.provider !== provider)
      const keys = mask ? [...others, { provider, mask, status }] : others

      commit('setUser', { ...state.user, aiProviderKeys: keys })
    },

    async checkAiProviderKey ({ dispatch, commit }, { provider }) {
      try {
        const res = await client.query({
          operationName: 'checkAiProviderKey',
          query: CheckAiProviderKey,
          variables: { provider }
        })
        const result = res.checkAiProviderKey

        dispatch('setAiProviderKeyEntry', { provider, mask: result?.mask, status: result?.status })

        commit('snackbar/showMessage', {
          content: KEY_STATUS_MESSAGE[result?.status] || 'The provider rejected this API key.',
          timeout: 4000,
          color: result?.status === 'ACTIVE' ? 'success' : 'red'
        }, { root: true })

        return result
      } catch (e) {
        commit('snackbar/showMessage', {
          content: 'Could not check the API key. Please try again.',
          timeout: 6000,
          color: 'red'
        }, { root: true })

        return { status: null }
      }
    },

    async setAiProviderKey ({ dispatch, commit }, { provider, apiKey }) {
      try {
        const res = await client.query({
          operationName: 'setAiProviderKey',
          query: SetAiProviderKey,
          variables: { provider, apiKey }
        })
        const result = res.setAiProviderKey

        if (result?.status === 'REJECTED') {
          return result
        }

        dispatch('setAiProviderKeyEntry', { provider, mask: result.mask, status: result.status })

        commit('snackbar/showMessage', {
          content: KEY_STATUS_MESSAGE[result.status] || 'API key saved.',
          timeout: 4000,
          color: result.status === 'ACTIVE' ? 'success' : 'warning'
        }, { root: true })

        return result
      } catch (e) {
        return { status: null, message: 'Could not save the API key. Please try again.' }
      }
    },

    async removeAiProviderKey ({ dispatch, commit }, { provider }) {
      try {
        await client.query({
          operationName: 'removeAiProviderKey',
          query: RemoveAiProviderKey,
          variables: { provider }
        })

        dispatch('setAiProviderKeyEntry', { provider, mask: null })

        commit('snackbar/showMessage', {
          content: 'API key removed.',
          timeout: 4000,
          color: 'success'
        }, { root: true })
      } catch (e) {
        commit('snackbar/showMessage', {
          content: 'Failed to remove the API key. Please try again.',
          timeout: 6000,
          color: 'red'
        }, { root: true })
      }
    },

    async generateApiKey ({ commit, state }, { expiresInDays = null } = {}) {
      try {
        const res = await client.query({
          operationName: 'generateApiKey',
          query: GenerateApiKey,
          variables: { expiresInDays }
        })

        const apiKey = res.generateApiKey.apiKey
        const expiresAt = res.generateApiKey.expiresAt || null

        commit('setUser', {
          ...state.user,
          hasMcpApiKey: true,
          apiKeyExpiresAt: expiresAt
        })

        commit('snackbar/showMessage', {
          content: 'API Key generated successfully! Make sure to copy it now - you won\'t be able to see it again.',
          timeout: 6000,
          color: 'success'
        }, { root: true })

        return { apiKey, expiresAt }
      } catch (e) {
        commit('snackbar/showMessage', {
          content: 'Failed to generate API key. Please try again.',
          timeout: 6000,
          color: 'red'
        }, { root: true })

        return null
      }
    },

    async revokeApiKey ({ commit, state }) {
      try {
        await client.query({
          operationName: 'revokeApiKey',
          query: RevokeApiKey
        })

        commit('setUser', {
          ...state.user,
          hasMcpApiKey: false,
          apiKeyExpiresAt: null
        })

        commit('snackbar/showMessage', {
          content: 'API key revoked. Clients still using it lose access within a minute.',
          timeout: 6000,
          color: 'success'
        }, { root: true })

        return true
      } catch (e) {
        commit('snackbar/showMessage', {
          content: 'Failed to revoke the API key. Please try again.',
          timeout: 6000,
          color: 'red'
        }, { root: true })

        return false
      }
    },

    async listUsers (
      { commit, dispatch, state },
      {
        limit = 100,
        nextToken = null,
        search = null,
        sortBy = null,
        append = false,
        fetchAllForSearch = false,
        _requestId
      } = {}
    ) {
      const requestId = _requestId ?? ++latestListUsersRequestId
      const isLatest = () => requestId === latestListUsersRequestId

      commit('setLoadingUsers', true)
      commit('setCurrentSortBy', sortBy)

      const isNewSearchInitiated = (search !== null && search !== state.searchTerm && !append)

      if (isNewSearchInitiated) {
        commit('setUsers', { items: [], append: false })
        commit('setNextTokenUsers', null)
      } else if (search !== null && !append && !fetchAllForSearch) {
        commit('setUsers', { items: [], append: false })
        commit('setNextTokenUsers', null)
      }

      commit('setCurrentSearchTerm', search || '')

      try {
        const variables = { limit, sortBy: state.sortBy }

        if (nextToken) {
          variables.nextToken = nextToken
        }
        if (search && search.trim() !== '') {
          variables.search = search.trim()
        }

        const res = await client.query({
          operationName: 'listUsers',
          query: ListUsers,
          variables
        })

        if (!isLatest()) {
          return
        }

        const listUsersData = res.listUsers

        if (listUsersData) {
          if (!fetchAllForSearch) {
            await fetchRemainingNestedPages(listUsersData.items)

            if (!isLatest()) {
              return
            }
          }
          commit('setUsers', { items: listUsersData.items, append: append || (fetchAllForSearch && nextToken) })
          commit('setNextTokenUsers', listUsersData.nextToken)

          if (fetchAllForSearch && listUsersData.nextToken) {
            await dispatch('listUsers', {
              limit,
              nextToken: listUsersData.nextToken,
              search,
              sortBy: state.sortBy,
              append: true,
              fetchAllForSearch: true,
              _requestId: requestId
            })
          }
        }
        return listUsersData
      } catch (error) {
        if (!isLatest()) {
          return
        }
        commit('snackbar/showMessage', {
          content: 'Error fetching users',
          timeout: 6000,
          color: 'red'
        }, { root: true })
        commit('setNextTokenUsers', null)
      } finally {
        if (isLatest() && (!fetchAllForSearch || !state.nextTokenUsers)) {
          commit('setLoadingUsers', false)
        }
      }
    }
  }
}

export { UserSortableField, SortOrder }
